/* global Word */

import { generateUUID } from "./uuid";

export const PARAGRAPH_TAG_PREFIX = "mss:p:";
export const PARAGRAPH_ANCHOR_CC_TITLE = "MSS Paragraph Anchor";

export interface TaggingResult {
  tagged: number;
  skipped: number;
  total: number;
}

export interface TaggingProgressEvent {
  phase: "loading" | "tagging" | "complete";
  taggedSoFar: number;
  totalToTag: number;
  totalParagraphs: number;
}

export interface TaggingOptions {
  batchSize?: number;
  onProgress?: (event: TaggingProgressEvent) => void;
}

export interface ParagraphTagStatus {
  index: number;
  uniqueLocalId: string;
  textPreview: string;
  tag: string | null;
}

export interface DiagnosticReport {
  totalParagraphs: number;
  taggedCount: number;
  untaggedCount: number;
  paragraphs: ParagraphTagStatus[];
}

/**
 * Wraps every untagged paragraph in a content control tagged `mss:p:<UUID>`.
 * Idempotent — paragraphs that already have an `mss:p:` control are skipped.
 *
 * Uses BoundingBox appearance for POC visibility; production should use Hidden.
 */
export async function tagAllParagraphs(options?: TaggingOptions): Promise<TaggingResult> {
  const BATCH_SIZE = options?.batchSize ?? 500;

  try {
    return await Word.run(async (context) => {
      // Step 1: Find all paragraphs already tagged with mss:p:
      const allControls = context.document.contentControls.getByTitle(PARAGRAPH_ANCHOR_CC_TITLE);
      allControls.load("items/tag,items/placeholderText");
      await context.sync();

      const mssControls = allControls.items.filter((cc) =>
        cc.tag?.startsWith(PARAGRAPH_TAG_PREFIX)
      );

      for (const cc of mssControls) {
        cc.paragraphs.load("uniqueLocalId");
        if (cc.placeholderText !== "") {
          cc.placeholderText = "";
        }
      }
      await context.sync();

      const alreadyTagged = new Set<string>();
      for (const cc of mssControls) {
        for (const p of cc.paragraphs.items) {
          alreadyTagged.add(p.uniqueLocalId);
        }
      }

      // Step 2: Load all body paragraphs
      const paragraphCollection = context.document.body.paragraphs;
      paragraphCollection.load("items/uniqueLocalId");
      await context.sync();

      const paragraphs = paragraphCollection.items;
      let skipped = 0;

      // Step 3: Collect untagged paragraphs
      const untagged: Word.Paragraph[] = [];
      for (const paragraph of paragraphs) {
        if (alreadyTagged.has(paragraph.uniqueLocalId)) {
          skipped++;
        } else {
          untagged.push(paragraph);
        }
      }

      options?.onProgress?.({
        phase: "tagging",
        taggedSoFar: 0,
        totalToTag: untagged.length,
        totalParagraphs: paragraphs.length,
      });

      // Step 4: Insert in batches so each sync is bounded (~2-3s each) and progress is reported
      let tagged = 0;
      for (let i = 0; i < untagged.length; i += BATCH_SIZE) {
        const batch = untagged.slice(i, i + BATCH_SIZE);
        for (const paragraph of batch) {
          const cc = paragraph.insertContentControl();
          cc.tag = PARAGRAPH_TAG_PREFIX + generateUUID();
          cc.title = PARAGRAPH_ANCHOR_CC_TITLE;
          cc.appearance = Word.ContentControlAppearance.hidden;
          cc.placeholderText = "";
        }
        try {
          await context.sync();
        } catch (batchErr) {
          // A paragraph may have been deleted mid-tagging; log and continue
          console.warn(
            `[tagAllParagraphs] batch ${i / BATCH_SIZE} sync error (continuing):`,
            batchErr
          );
        }
        tagged += batch.length;
        options?.onProgress?.({
          phase: "tagging",
          taggedSoFar: tagged,
          totalToTag: untagged.length,
          totalParagraphs: paragraphs.length,
        });
      }

      options?.onProgress?.({
        phase: "complete",
        taggedSoFar: tagged,
        totalToTag: untagged.length,
        totalParagraphs: paragraphs.length,
      });

      return { tagged, skipped, total: paragraphs.length };
    });
  } catch (error) {
    console.error(`[tagAllParagraphs] Error:`, error);
    throw new Error(
      `Failed to tag paragraphs: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

async function _logTaggedOoxmlInContext(context: Word.RequestContext): Promise<void> {
  const ooxml = context.document.body.getOoxml();
  const allControls = context.document.contentControls.getByTitle(PARAGRAPH_ANCHOR_CC_TITLE);
  allControls.load("items/tag");
  await context.sync();

  const mssControls = allControls.items.filter((cc) => cc.tag?.startsWith(PARAGRAPH_TAG_PREFIX));
  for (const cc of mssControls) {
    cc.paragraphs.load("text");
  }
  await context.sync();

  const textByTag = new Map<string, string>();
  for (const cc of mssControls) {
    textByTag.set(cc.tag, cc.paragraphs.items.map((p) => p.text).join(" "));
  }

  const tags: string[] = [];
  const re = /<w:tag w:val="(mss:p:[^"]+)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(ooxml.value)) !== null) {
    tags.push(m[1]);
  }
}

/**
 * Reads the raw OOXML body and extracts all w:tag values matching mss:p:.
 * Stronger than diagnoseParagraphTags — proves the serialized file structure,
 * not just the Office.js object model.
 */
export async function logTaggedOoxml(): Promise<void> {
  try {
    await Word.run((context) => _logTaggedOoxmlInContext(context));
  } catch (error) {
    console.error("[logTaggedOoxml] Error:", error);
    throw new Error(
      `Failed to read OOXML: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Returns a Map of mss:p: tag → session-scoped uniqueLocalId for every tagged paragraph.
 * Use this to bridge from the persistent tag to the Word SDK's runtime paragraph identity.
 *
 * Redux migration note: dispatch paragraphAnchors/hydrated with this map on startup,
 * and paragraphAnchors/cleared before re-hydrating on each document open, since
 * uniqueLocalId is session-scoped and invalid across reopens.
 */
export async function getTagToLocalIdMap(): Promise<Map<string, string>> {
  try {
    return await Word.run(async (context) => {
      const allControls = context.document.contentControls.getByTitle(PARAGRAPH_ANCHOR_CC_TITLE);
      allControls.load("items/tag");
      await context.sync();

      const mssControls = allControls.items.filter((cc) =>
        cc.tag?.startsWith(PARAGRAPH_TAG_PREFIX)
      );

      for (const cc of mssControls) {
        cc.paragraphs.load("uniqueLocalId");
      }
      await context.sync();

      const map = new Map<string, string>();
      for (const cc of mssControls) {
        if (cc.paragraphs.items.length > 0) {
          map.set(cc.tag, cc.paragraphs.items[0].uniqueLocalId);
        }
      }

      return map;
    });
  } catch (error) {
    console.error(`[getTagToLocalIdMap] Error:`, error);
    throw new Error(
      `Failed to build tag map: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Returns a Map of session-scoped uniqueLocalId → mss:p: tag for every tagged paragraph.
 * Inverse of getTagToLocalIdMap — use this to look up a paragraph's persistent tag
 * from its runtime Word SDK identity.
 */
export async function getLocalIdToTagMap(): Promise<Map<string, string>> {
  try {
    return await Word.run(async (context) => {
      const allControls = context.document.contentControls.getByTitle(PARAGRAPH_ANCHOR_CC_TITLE);
      allControls.load("items/tag");
      await context.sync();

      const mssControls = allControls.items.filter((cc) =>
        cc.tag?.startsWith(PARAGRAPH_TAG_PREFIX)
      );

      for (const cc of mssControls) {
        cc.paragraphs.load("uniqueLocalId");
      }
      await context.sync();

      const map = new Map<string, string>();
      for (const cc of mssControls) {
        if (cc.paragraphs.items.length > 0) {
          map.set(cc.paragraphs.items[0].uniqueLocalId, cc.tag);
        }
      }

      return map;
    });
  } catch (error) {
    console.error(`[getLocalIdToTagMap] Error:`, error);
    throw new Error(
      `Failed to build local-id map: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Returns a diagnostic report of paragraph tag coverage.
 */
export async function diagnoseParagraphTags(): Promise<DiagnosticReport> {
  try {
    return await Word.run(async (context) => {
      // Load all mss:p: content controls and their wrapped paragraph IDs
      const allControls = context.document.contentControls.getByTitle(PARAGRAPH_ANCHOR_CC_TITLE);
      allControls.load("items/tag");
      await context.sync();

      const mssControls = allControls.items.filter((cc) =>
        cc.tag?.startsWith(PARAGRAPH_TAG_PREFIX)
      );

      for (const cc of mssControls) {
        cc.paragraphs.load("uniqueLocalId");
      }
      await context.sync();

      const tagByParagraphId = new Map<string, string>();
      for (const cc of mssControls) {
        for (const p of cc.paragraphs.items) {
          tagByParagraphId.set(p.uniqueLocalId, cc.tag);
        }
      }

      // Load all paragraphs with text for preview
      const paragraphCollection = context.document.body.paragraphs;
      paragraphCollection.load("items/uniqueLocalId,items/text");
      await context.sync();

      const paragraphs = paragraphCollection.items;
      const rows: ParagraphTagStatus[] = paragraphs.map((p, index) => ({
        index,
        uniqueLocalId: p.uniqueLocalId,
        textPreview: p.text.slice(0, 60),
        tag: tagByParagraphId.get(p.uniqueLocalId) ?? null,
      }));

      const taggedCount = rows.filter((r) => r.tag !== null).length;
      const untaggedCount = rows.length - taggedCount;

      return {
        totalParagraphs: rows.length,
        taggedCount,
        untaggedCount,
        paragraphs: rows,
      };
    });
  } catch (error) {
    console.error(`[diagnoseParagraphTags] Error:`, error);
    throw new Error(
      `Failed to diagnose paragraph tags: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}
